/* eslint-disable react/no-array-index-key */
import React from 'react';
import Dropdown from 'rc-dropdown';
import Menu, { Item as MenuItem } from 'rc-menu';
import styled from '@emotion/styled';
import IcomoonReact from 'icomoon-react';
import PropTypes from 'prop-types';
import 'rc-dropdown/assets/index.css';

import iconSet from '../../../../shared/images/teambuildr-selection.json';
import { PACE_MENU_ITEMS, formatPaceLabel } from '../../utils/pacingPercentages';

const DropdownIcon = styled('div')`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 5px;
`;

const DropdownContainerPrimary = styled('div')`
  display: flex;
  align-items: center;
  ${(props) => props.visible && 'cursor: pointer;'}
`;

const MenuWrapper = styled('div')`
  display: flex;
  flex-direction: column;
`;

const CustomDivider = styled.div`
  height: 1px;
  margin: 8px 4px;
  background: #EEEEEE;
`;

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  background: ${(props) => props.backgroundColor && props.backgroundColor};
  border-radius: 4px;
  padding: 5px;
  background: white;
  :hover {
    background: ${(props) => props.isSelectable && '#FCFCFC'}
  }
`;

const DeleteIconContainer = styled.div`
  display: flex;
  align-items: center;
  background: white;
  border-radius: 4px;
  padding: 4px;
`;

const ArrowDiv = styled('div')`
  display: flex;
  justify-content: space-between;
  width: 100%;
  align-items: center;
`;

const MenuText = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 12pt;
  color: ${(props) => (props.color)};
  margin-bottom: 0px;
  font-size: 16px;
`;

const SwapPropDropdownSAQC = ({
  size,
  primaryRowSwitch,
  setPrimaryRowSwitch,
  rowName,
  updateColumnName,
  setIntensityUnit,
  setPaceUnit,
  paceUnit,
  name,
  unit,
  visible,
}) => {
  const menuConfig = [
    {
      condition: rowName === 'pace',
      items: PACE_MENU_ITEMS.map((paceName) => ({
        label: formatPaceLabel(paceName),
        value: paceName,
        isSelectable: paceName !== 'Running' && paceName !== 'Rowing',
        selected: paceUnit === paceName,
        action: () => setPaceUnit(paceName),
      })),
    },
    {
      condition: ['intensity (bpm)', 'intensity (RPE)', 'intensity (%)', 'intensity (W)'].includes(rowName),
      items: [
        {
          label: 'Heart Rate (bpm)',
          intensityType: 'bpm',
          selected: primaryRowSwitch['intensity (bpm)'],
          isSelectable: true,
        },
        {
          label: 'RPE',
          intensityType: 'RPE',
          selected: primaryRowSwitch['intensity (RPE)'],
          isSelectable: true,
        },
        {
          label: 'Percentage (%)',
          intensityType: '%',
          selected: primaryRowSwitch['intensity (%)'],
          isSelectable: true,
        },
        {
          label: 'Watts (W)',
          intensityType: 'W',
          selected: primaryRowSwitch['intensity (W)'],
          isSelectable: true,
        },
      ].map(({
        label, intensityType, selected, isSelectable,
      }) => ({
        label,
        selected,
        isSelectable,
        action: () => {
          setIntensityUnit(intensityType);
          updateColumnName(rowName, `intensity (${intensityType})`);
        },
      })),
    },
  ];

  // Get the format items based on the condition
  const formatItems = menuConfig
    .filter((config) => config.condition)
    .flatMap((config) => config.items);

  const menu = (
    <Menu
      style={{
        padding: 8,
        width: 229,
        overflowY: 'auto',
        marginBottom: '0px',
        border: '1px solid #eeeeee',
        gap: 8,
        background: 'white',
        maxHeight: formatItems.length > 10 ? 300 : 'auto',
        scrollbarWidth: 'thin',
      }}
    >
      <MenuWrapper>
        {formatItems.map(({
          label, value, action, selected, isSelectable,
        }, index) => (
          <MenuItem
            key={value || label || index}
            style={{
              fontWeight: (value === 'Running' || value === 'Rowing') && 700,
              paddingLeft: (rowName === 'pace' && value !== 'Running' && value !== 'Rowing') && 12,
              paddingRight: (rowName === 'pace' && value !== 'Running' && value !== 'Rowing') && 12,
              cursor: (isSelectable && value !== 'Running' && value !== 'Rowing') ? 'pointer' : 'default',
              color: (isSelectable && value !== 'Running' && value !== 'Rowing') ? '#444444' : '#9E9E9E',
              background: 'white',
            }}
            onClick={isSelectable ? action : undefined}
          >
            <IconContainer isSelectable={isSelectable && value !== 'Running' && value !== 'Rowing'}>
              <ArrowDiv>
                <MenuText color={(isSelectable && value !== 'Running' && value !== 'Rowing' && !selected) ? '#444444' : '#9E9E9E'}>{label}</MenuText>
                {selected && isSelectable && (
                  <IcomoonReact iconSet={iconSet} size={12} icon='checkmark' color='#9E9E9E' />
                )}
              </ArrowDiv>
            </IconContainer>
          </MenuItem>
        ))}
        {formatItems.length > 0 && <CustomDivider />}
        <MenuItem
          style={{
            cursor: 'pointer', display: 'flex', width: '100%', background: 'white',
          }}
          onClick={() => {
            const flippedStatus = !primaryRowSwitch[rowName];
            setPrimaryRowSwitch({ ...primaryRowSwitch, [rowName]: flippedStatus });
          }}
        >
          <IconContainer>
            <IcomoonReact
              iconSet={iconSet}
              size={17}
              icon='trashcan'
              color='#FF0505'
              style={{ marginRight: 5 }}
            />
            <ArrowDiv>
              <MenuText>Delete</MenuText>
            </ArrowDiv>
          </IconContainer>
        </MenuItem>
      </MenuWrapper>
    </Menu>
  );

  const deleteOnlyMenu = (
    <Menu
      style={{
        width: 229,
        overflowY: 'auto',
        marginBottom: '0px',
        border: '1px solid #eeeeee',
        background: 'white',
      }}
    >
      <MenuWrapper>
        <MenuItem
          style={{ padding: 8, cursor: 'pointer', background: 'white' }}
          onClick={() => {
            const flippedStatus = !primaryRowSwitch[rowName];
            const newSwitch = { ...primaryRowSwitch };
            newSwitch[rowName] = flippedStatus;
            setPrimaryRowSwitch(newSwitch);
          }}
        >
          <DeleteIconContainer>
            <IcomoonReact
              iconSet={iconSet}
              size={17}
              icon='trashcan'
              color='#FF0505'
              style={{ marginRight: 5 }}
            />
            <ArrowDiv>
              <MenuText>Delete</MenuText>
            </ArrowDiv>
          </DeleteIconContainer>
        </MenuItem>
      </MenuWrapper>
    </Menu>
  );

  return (
    <Dropdown
      trigger={visible ? ['click'] : []} /** Disable trigger if not visible */
      overlay={rowName === 'prescribed value' || rowName === 'rest' || rowName === 'rest per rep' || rowName === 'reps' ? deleteOnlyMenu : menu}
      aria-haspopup='true'
    >
      <DropdownContainerPrimary onClick={(e) => e.stopPropagation()} visible={visible}>
        <DropdownIcon padding={size}>
          {name}
          {' '}
          {unit || null}
          {!visible ? null : (
            <IcomoonReact
              iconSet={iconSet}
              size={7}
              style={{ minWidth: '7px' }}
              icon='down-arrow'
              color='black'
            />
          )}
        </DropdownIcon>
      </DropdownContainerPrimary>
    </Dropdown>
  );
};

SwapPropDropdownSAQC.propTypes = {
  size: PropTypes.number.isRequired,
  primaryRowSwitch: PropTypes.instanceOf(Object).isRequired,
  setPrimaryRowSwitch: PropTypes.func.isRequired,
  rowName: PropTypes.string.isRequired,
  updateColumnName: PropTypes.func.isRequired,
  setIntensityUnit: PropTypes.func.isRequired,
  setPaceUnit: PropTypes.func.isRequired,
  paceUnit: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  unit: PropTypes.string.isRequired,
  visible: PropTypes.bool,
};

SwapPropDropdownSAQC.defaultProps = {
  visible: true,
};

export default SwapPropDropdownSAQC;
